import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import Select from 'react-select';
import { X, Copy, Check, Settings } from 'lucide-react';
import { categoriasAPI, tiposServicioAPI } from '../services/api';
import { invalidateTiposServicio, invalidateCategorias } from '../services/cachedCatalogs';
import { selectStyles } from '../utils/adminShared';
import { useConfirm, Tooltip } from './ui';
import CategoriasManagerModal from './CategoriasManagerModal';

// Estilos del select alineados a la altura de los .input (~40px)
const selectStylesTall = {
    ...selectStyles,
    control: (base, state) => ({
        ...selectStyles.control(base, state),
        minHeight: '42px',
    }),
};


const ServiciosPanel = () => {
    const confirm = useConfirm();
    const [loading, setLoading] = useState(true);
    const [categorias, setCategorias] = useState([]);
    const [tiposServicio, setTiposServicio] = useState([]);
    const [nuevoTipo, setNuevoTipo] = useState({ nombre: '', descripcion: '', categoria_id: '' });
    const [showCategoriasModal, setShowCategoriasModal] = useState(false);
    const [filtroCategoria, setFiltroCategoria] = useState(null);
    const [busqueda, setBusqueda] = useState('');
    // Paginacion client-side del listado (evita renderizar 100+ inputs a la vez).
    const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
    const [pageSize, setPageSize] = useState(25);
    const [currentPage, setCurrentPage] = useState(1);

    // Cambios pendientes por servicio — requieren confirmacion explicita
    const [pendingServicios, setPendingServicios] = useState({});

    // Helpers para edicion con confirmacion (servicios)
    const getServVal = (tipo, field) => (
        pendingServicios[tipo.id]?.[field] !== undefined
            ? pendingServicios[tipo.id][field]
            : tipo[field] ?? ''
    );
    const setServVal = (id, field, value) => {
        setPendingServicios(p => ({ ...p, [id]: { ...(p[id] || {}), [field]: value } }));
    };
    const hasServChanges = (id) => Boolean(pendingServicios[id] && Object.keys(pendingServicios[id]).length > 0);
    const confirmServChanges = async (id) => {
        const changes = pendingServicios[id];
        if (!changes) return;
        await actualizarTipoServicio(id, changes);
        setPendingServicios(p => { const n = { ...p }; delete n[id]; return n; });
        toast.success('Servicio actualizado');
    };
    const discardServChanges = (id) => {
        setPendingServicios(p => { const n = { ...p }; delete n[id]; return n; });
    };

    useEffect(() => {
        cargarDatos();
    }, []);

    const cargarDatos = async () => {
        setLoading(true);
        try {
            const [categoriasRes, tiposRes] = await Promise.all([
                categoriasAPI.getAll(true),
                tiposServicioAPI.getAll(true)
            ]);
            setCategorias(categoriasRes.data || []);
            setTiposServicio(tiposRes.data || []);
        } catch (err) {
            console.error('Error al cargar datos:', err);
        } finally {
            setLoading(false);
        }
    };

    // === TIPOS DE SERVICIO ===
    const agregarTipoServicio = async () => {
        if (!nuevoTipo.nombre.trim()) {
            toast.error('El nombre es obligatorio');
            return;
        }
        if (!nuevoTipo.categoria_id) {
            toast.error('La categoría es obligatoria');
            return;
        }
        try {
            await tiposServicioAPI.create(nuevoTipo);
            invalidateTiposServicio();
            setNuevoTipo({ nombre: '', descripcion: '', categoria_id: '' });
            cargarDatos();
            toast.success('Servicio creado');
        } catch (err) {
            toast.error('Error: ' + err.message);
        }
    };

    const actualizarTipoServicio = async (id, data) => {
        try {
            await tiposServicioAPI.update(id, data);
            invalidateTiposServicio();
            setTiposServicio(prev => prev.map(t => t.id === id ? { ...t, ...data } : t));
        } catch (err) {
            toast.error('Error: ' + err.message);
            cargarDatos();
        }
    };

    const eliminarTipoServicio = async (id, nombre) => {
        const ok = await confirm({
            title: '¿Eliminar servicio?',
            message: <p>Vas a eliminar el servicio <strong>{nombre}</strong>. Esta acción no se puede deshacer.</p>,
            confirmText: 'Eliminar',
            danger: true,
        });
        if (!ok) return;
        try {
            await tiposServicioAPI.delete(id);
            invalidateTiposServicio();
            toast.success('Servicio eliminado');
            cargarDatos();
        } catch (err) {
            toast.error('Error: ' + err.message);
        }
    };

    const duplicarServicio = async (tipo) => {
        try {
            await tiposServicioAPI.create({
                nombre: `${tipo.nombre} (copia)`,
                descripcion: tipo.descripcion || '',
                categoria_id: tipo.categoria_id || ''
            });
            invalidateTiposServicio();
            cargarDatos();
            toast.success('Servicio duplicado');
        } catch (err) {
            toast.error('Error: ' + err.message);
        }
    };

    const busquedaNorm = busqueda.trim().toLowerCase();
    const serviciosFiltrados = tiposServicio.filter(t => {
        if (filtroCategoria && t.categoria_id !== filtroCategoria) return false;
        if (busquedaNorm && !String(t.nombre || '').toLowerCase().includes(busquedaNorm)) return false;
        return true;
    });

    const totalPaginas = Math.max(1, Math.ceil(serviciosFiltrados.length / pageSize));
    const paginaActual = Math.min(currentPage, totalPaginas);
    const serviciosPagina = serviciosFiltrados.slice((paginaActual - 1) * pageSize, paginaActual * pageSize);

    // Reset a pagina 1 cuando cambia el filtro, la busqueda o el pageSize.
    useEffect(() => { setCurrentPage(1); }, [filtroCategoria, pageSize, busqueda]);

    if (loading) {
        return <div className="loading">Cargando servicios...</div>;
    }

    return (
    <>
        <div className="card">
            <div className="card-header">
                <h2>Servicios</h2>
            </div>
            <div className="admin-content">
                <div className="admin-section-header">
                    <h3 className="h3">Agregar Servicio</h3>
                    <button
                        className="btn small btn-outline-primary"
                        type="button"
                        onClick={() => setShowCategoriasModal(true)}
                    >
                        <Settings size={14} />
                        Gestionar Categorías
                    </button>
                </div>
                <div className="servicios-form-grid">
                    <label className="field">
                        <span className="label">Nombre *</span>
                        <input
                            className="input"
                            type="text"
                            value={nuevoTipo.nombre}
                            onChange={(e) => setNuevoTipo(n => ({ ...n, nombre: e.target.value }))}
                        />
                    </label>
                    <label className="field">
                        <span className="label">Descripción</span>
                        <input
                            className="input"
                            type="text"
                            value={nuevoTipo.descripcion}
                            onChange={(e) => setNuevoTipo(n => ({ ...n, descripcion: e.target.value }))}
                        />
                    </label>
                    <div className="field">
                        <span className="label">Categoría *</span>
                        <Select
                            styles={selectStylesTall}
                            options={categorias.filter(c => c.activo).map(cat => ({
                                value: cat.id,
                                label: cat.nombre
                            }))}
                            value={
                                nuevoTipo.categoria_id
                                    ? { value: nuevoTipo.categoria_id, label: categorias.find(c => c.id === parseInt(nuevoTipo.categoria_id))?.nombre || '' }
                                    : null
                            }
                            onChange={(selected) => setNuevoTipo(n => ({ ...n, categoria_id: selected?.value || '' }))}
                            placeholder="Seleccionar categoría..."
                            isSearchable
                            noOptionsMessage={() => 'Sin opciones'}
                        />
                    </div>
                    <button className="btn neon servicios-form-submit" type="button" onClick={agregarTipoServicio}>
                        + Agregar
                    </button>
                </div>

                <div className="divider" />

                <div className="admin-section-header">
                    <h3 className="h3">Servicios Existentes ({serviciosFiltrados.length})</h3>
                    <div className="admin-filtros-inline">
                        <input
                            className="input admin-buscador-inline"
                            type="search"
                            value={busqueda}
                            onChange={(e) => setBusqueda(e.target.value)}
                            placeholder="Buscar por nombre..."
                            aria-label="Buscar servicio"
                        />
                        <Select
                            styles={selectStyles}
                            options={[
                                { value: null, label: 'Todas las categorías' },
                                ...categorias.filter(c => c.activo).map(cat => ({
                                    value: cat.id,
                                    label: cat.nombre
                                }))
                            ]}
                            value={
                                filtroCategoria
                                    ? { value: filtroCategoria, label: categorias.find(c => c.id === filtroCategoria)?.nombre || '' }
                                    : { value: null, label: 'Todas las categorías' }
                            }
                            onChange={(selected) => setFiltroCategoria(selected?.value || null)}
                            placeholder="Filtrar por categoría..."
                            isSearchable={false}
                            noOptionsMessage={() => 'Sin opciones'}
                        />
                    </div>
                </div>
                <div className="admin-list">
                    {serviciosPagina.map(tipo => {
                        const dirty = hasServChanges(tipo.id);
                        const catId = getServVal(tipo, 'categoria_id');
                        return (
                        <div key={tipo.id} className={`admin-item ${!tipo.activo ? 'inactive' : ''} ${dirty ? 'has-pending-changes' : ''}`}>
                            <div className="admin-item-content">
                                <input
                                    className="input"
                                    type="text"
                                    value={getServVal(tipo, 'nombre')}
                                    onChange={(e) => setServVal(tipo.id, 'nombre', e.target.value)}
                                />
                                <input
                                    className="input"
                                    type="text"
                                    value={getServVal(tipo, 'descripcion')}
                                    onChange={(e) => setServVal(tipo.id, 'descripcion', e.target.value)}
                                    placeholder="Descripción..."
                                />
                                <div className="admin-item-select">
                                    <Select
                                        styles={selectStylesTall}
                                        options={[
                                            { value: '', label: 'Sin categoría' },
                                            ...categorias.filter(c => c.activo).map(cat => ({
                                                value: cat.id,
                                                label: cat.nombre
                                            }))
                                        ]}
                                        value={
                                            catId
                                                ? { value: catId, label: categorias.find(c => c.id === catId)?.nombre || '' }
                                                : { value: '', label: 'Sin categoría' }
                                        }
                                        onChange={(selected) => setServVal(tipo.id, 'categoria_id', selected?.value || null)}
                                        placeholder="Categoría..."
                                        isSearchable
                                        noOptionsMessage={() => 'Sin opciones'}
                                        menuPortalTarget={document.body}
                                        menuPosition="fixed"
                                    />
                                </div>
                            </div>
                            <div className="admin-item-actions">
                                {dirty && (
                                    <>
                                        <button
                                            className="btn success small"
                                            onClick={() => confirmServChanges(tipo.id)}
                                        >
                                            <Check size={14} />
                                            Actualizar
                                        </button>
                                        <Tooltip content="Descartar cambios">
                                            <button
                                                className="btn ghost small"
                                                onClick={() => discardServChanges(tipo.id)}
                                                aria-label="Descartar cambios"
                                            >
                                                <X size={14} />
                                            </button>
                                        </Tooltip>
                                    </>
                                )}
                                <Tooltip content="Duplicar servicio">
                                    <button
                                        className="btn ghost small"
                                        onClick={() => duplicarServicio(tipo)}
                                        aria-label="Duplicar servicio"
                                    >
                                        <Copy size={14} />
                                    </button>
                                </Tooltip>
                                <button
                                    className={`btn ghost small ${tipo.activo ? '' : 'success'}`}
                                    onClick={() => actualizarTipoServicio(tipo.id, { activo: !tipo.activo })}
                                >
                                    {tipo.activo ? 'Desactivar' : 'Activar'}
                                </button>
                                <button
                                    className="btn ghost small danger"
                                    onClick={() => eliminarTipoServicio(tipo.id, tipo.nombre)}
                                >
                                    Eliminar
                                </button>
                            </div>
                        </div>
                        );
                    })}
                    {serviciosFiltrados.length === 0 && (
                        <p className="empty-message">No hay servicios{filtroCategoria ? ' en esta categoría' : ''}</p>
                    )}
                </div>

                {/* Paginacion client-side */}
                {serviciosFiltrados.length > 0 && (
                    <div className="admin-pagination">
                        <div className="admin-pagination-info">
                            Mostrando <strong>{(paginaActual - 1) * pageSize + 1}</strong>
                            {' – '}
                            <strong>{Math.min(paginaActual * pageSize, serviciosFiltrados.length)}</strong>
                            {' de '}
                            <strong>{serviciosFiltrados.length}</strong>
                        </div>
                        <div className="admin-pagination-controls">
                            <label className="admin-pagination-size">
                                <span className="text-xs text-muted">Por página:</span>
                                <select
                                    className="input"
                                    value={pageSize}
                                    onChange={(e) => setPageSize(parseInt(e.target.value, 10))}
                                >
                                    {PAGE_SIZE_OPTIONS.map(n => <option key={n} value={n}>{n}</option>)}
                                </select>
                            </label>
                            <div className="admin-pagination-buttons">
                                <button
                                    type="button"
                                    className="btn ghost sm"
                                    disabled={paginaActual === 1}
                                    onClick={() => setCurrentPage(1)}
                                >« Inicio</button>
                                <button
                                    type="button"
                                    className="btn ghost sm"
                                    disabled={paginaActual === 1}
                                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                >‹</button>
                                <span className="admin-pagination-current">
                                    Página <strong>{paginaActual}</strong> de <strong>{totalPaginas}</strong>
                                </span>
                                <button
                                    type="button"
                                    className="btn ghost sm"
                                    disabled={paginaActual === totalPaginas}
                                    onClick={() => setCurrentPage(p => Math.min(totalPaginas, p + 1))}
                                >›</button>
                                <button
                                    type="button"
                                    className="btn ghost sm"
                                    disabled={paginaActual === totalPaginas}
                                    onClick={() => setCurrentPage(totalPaginas)}
                                >Fin »</button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>

        {/* Modal de Categorías — patrón unificado (CRUD inline con primitivo Modal) */}
        <CategoriasManagerModal
            open={showCategoriasModal}
            onClose={() => setShowCategoriasModal(false)}
            onChanged={() => { cargarDatos(); invalidateCategorias(); invalidateTiposServicio(); }}
            title="Gestionar categorías de servicios"
            placeholder="Ej: Diseño gráfico"
            countField="tipos_count"
            countLabel="servicio"
            api={{
                list: () => categoriasAPI.getAll(true),
                create: (data) => categoriasAPI.create(data),
                update: (id, data) => categoriasAPI.update(id, data),
                delete: (id) => categoriasAPI.delete(id)
            }}
        />
    </>
    );
};

export default ServiciosPanel;
